
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { BookOpen, AlertCircle, Tag, TrendingUp, Lightbulb } from "lucide-react";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Decision, useDecisions } from "@/hooks/useDecisions";
import { getReviewCounts } from "@/lib/review-classification";
import { clickableCardProps } from "@/lib/clickable-card";

interface DashboardStatsProps {
  decisions: Decision[];
  onNavigateToReviews: () => void;
  onNavigateToMyDecisions: () => void;
  onNavigateToMyDecisionsWithTag: (tag: string) => void;
  onNavigateToAnalytics: () => void;
  onNavigateToInsights: () => void;
}

export function DashboardStats({ 
  decisions, 
  onNavigateToReviews, 
  onNavigateToMyDecisions,
  onNavigateToMyDecisionsWithTag,
  onNavigateToAnalytics,
  onNavigateToInsights
}: DashboardStatsProps) {
  const { getCurrentRating } = useDecisions();
  
  // Calculate fully reviewed decisions (all scheduled reviews completed)
  const fullyReviewedDecisions = decisions.filter(d => 
    d.scheduled_reviews.length > 0 && 
    d.scheduled_reviews.every(r => r.status === "completed")
  ).length;
  
  // Calculate average current rating
  const avgCurrentRating = decisions.length > 0 
    ? Math.round((decisions.reduce((sum, d) => sum + getCurrentRating(d), 0) / decisions.length) * 10) / 10
    : 0;
  
  // Calculate average initial rating
  const avgInitialRating = decisions.length > 0 
    ? Math.round((decisions.reduce((sum, d) => sum + d.day0_rating, 0) / decisions.length) * 10) / 10
    : 0;
  
  // Calculate decisions created in last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recentDecisions = decisions.filter(d => new Date(d.created_at) >= thirtyDaysAgo);
  
  // Calculate recent vs all-time average rating trend
  const recentAvgRating = recentDecisions.length > 0 
    ? Math.round((recentDecisions.reduce((sum, d) => sum + d.day0_rating, 0) / recentDecisions.length) * 10) / 10
    : 0;
  
  const ratingTrend = recentDecisions.length > 0 && decisions.length > 0 
    ? recentAvgRating - avgInitialRating 
    : 0;
  
  const { dueNowCount } = getReviewCounts(decisions);
  const pendingReviews = dueNowCount;
  
  const completedReviews = decisions.reduce((sum, d) => 
    sum + d.scheduled_reviews.filter(r => r.status === "completed").length, 0
  );
  
  const totalReviews = decisions.reduce((sum, d) => sum + d.scheduled_reviews.length, 0);
  const reviewRate = totalReviews > 0 ? Math.round((completedReviews / totalReviews) * 100) : 0;

  // Generate insights (simplified version from Analytics)
  const generateInsight = () => {
    if (decisions.length === 0) return "Start making decisions to see insights";
    
    const insights = [];
    
    if (ratingTrend > 0.5) {
      insights.push("Your confidence in decisions is improving over time");
    }
    if (reviewRate > 80) {
      insights.push("Great job staying on top of your reviews!");
    }
    if (fullyReviewedDecisions > 0) {
      insights.push(`You've fully reviewed ${fullyReviewedDecisions} decisions`);
    }
    if (pendingReviews > 5) {
      insights.push("Consider catching up on pending reviews");
    }
    if (insights.length === 0) {
      insights.push("Keep logging decisions to unlock insights");
    }
    
    return insights[Math.floor(Math.random() * insights.length)];
  };

  const getRatingTrendColor = () => {
    if (ratingTrend > 0.1) return "text-green-600";
    if (ratingTrend < -0.1) return "text-red-600";
    return "text-gray-500";
  };

  const stats = [
    {
      title: "Total Decisions",
      value: decisions.length.toString(),
      description: `${fullyReviewedDecisions} fully reviewed`,
      icon: BookOpen,
      trend: recentDecisions.length > 0 ? `+${recentDecisions.length}` : null,
      trendTooltip: "Number of decisions made in the last 30 days",
      clickable: true,
      onClick: onNavigateToMyDecisions
    },
    {
      title: "Due Now",
      value: pendingReviews.toString(),
      description: "Reviews due today",
      icon: AlertCircle,
      trend: null,
      clickable: true,
      onClick: onNavigateToReviews
    },
    {
      title: "Current Confidence",
      value: avgCurrentRating.toString(),
      description: `Lifetime avg: ${avgInitialRating}`,
      icon: TrendingUp,
      trend: ratingTrend !== 0 ? (ratingTrend > 0 ? `+${ratingTrend.toFixed(1)}` : ratingTrend.toFixed(1)) : null,
      trendTooltip: "Avg confidence of initial decisions made last 30 days vs. all-time average",
      trendColor: getRatingTrendColor(),
      clickable: true,
      onClick: onNavigateToAnalytics
    },
    {
      title: "Insights",
      value: generateInsight(),
      description: "Tap to see more",
      icon: Lightbulb,
      trend: null,
      clickable: true,
      onClick: onNavigateToInsights
    }
  ];

  // Get popular tags
  const tagCounts = decisions.reduce<Record<string, number>>((acc, decision) => {
    decision.tags?.filter(tag => tag?.name).forEach(tag => {
      acc[tag.name] = (acc[tag.name] || 0) + 1;
    });
    return acc;
  }, {});

  const popularTags = Object.entries(tagCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([tag, count]) => ({ name: tag, count }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card 
            key={stat.title} 
            className={stat.clickable ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
            {...(stat.clickable ? clickableCardProps(stat.onClick) : {})}
          >
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.trendColor || "text-muted-foreground"}`} />
            </CardHeader>
            <CardContent className="flex flex-col justify-end h-full">
              <div className={stat.title === "Insights" ? "text-sm font-medium leading-relaxed" : "text-2xl font-bold"}>
                {stat.value}
              </div>
              <div className="flex items-center justify-between mt-auto">
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
                {stat.trend && stat.trendTooltip ? (
                  <Tooltip>
                    <TooltipTrigger>
                      <span className={`text-xs ${stat.trendColor || "text-green-600"}`}>
                        {stat.trend}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{stat.trendTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : stat.trend ? (
                  <span className={`text-xs ${stat.trendColor || "text-green-600"}`}>
                    {stat.trend}
                  </span>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {popularTags.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-sm font-medium">Popular Tags</CardTitle>
              <CardDescription>Most used decision categories</CardDescription>
            </div>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {popularTags.map((tag) => (
                <Badge 
                  key={tag.name} 
                  variant="secondary" 
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => onNavigateToMyDecisionsWithTag(tag.name)}
                >
                  {tag.name} ({tag.count})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
